import { Download, Mic, MoreVertical, Trash2, Upload } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTranslation } from '@/hooks/useTranslation'
import {
	deleteEntry,
	list,
	type PendingRecording,
	subscribePendingRecordings,
} from '@/services/pendingRecordings'
import {
	formatRecordingByteSize,
	formatRecordingClock,
	formatRecordingRelativeTime,
} from '@/utils/recordingDisplayFormat'

export interface RecordingRecoveryBannerProps {
	projectId: string
	onResume: (file: File, recordingId: string) => void
}

export function RecordingRecoveryBanner({ projectId, onResume }: RecordingRecoveryBannerProps) {
	const { t } = useTranslation()
	const [entries, setEntries] = useState<PendingRecording[]>([])
	const [pendingDiscardId, setPendingDiscardId] = useState<string | null>(null)
	const [isDiscarding, setIsDiscarding] = useState(false)

	useEffect(() => {
		const refresh = () => {
			list(projectId)
				.then(setEntries)
				.catch(() => setEntries([]))
		}
		refresh()
		return subscribePendingRecordings(refresh)
	}, [projectId])

	const handleDownload = (entry: PendingRecording) => {
		const url = URL.createObjectURL(entry.blob)
		const a = document.createElement('a')
		a.href = url
		a.download = entry.suggestedFilename
		a.click()
		setTimeout(() => URL.revokeObjectURL(url), 10_000)
	}

	const handleConfirmDiscard = () => {
		if (pendingDiscardId === null) return
		setIsDiscarding(true)
		void deleteEntry(pendingDiscardId).finally(() => {
			setIsDiscarding(false)
			setPendingDiscardId(null)
		})
	}

	const handleResume = (entry: PendingRecording) => {
		const file = new File([entry.blob], entry.suggestedFilename, {
			type: entry.mimeType,
			lastModified: entry.recordedAt,
		})
		onResume(file, entry.id)
	}

	const showBanner = entries.length > 0
	if (!showBanner && pendingDiscardId === null) return null

	return (
		<>
			{showBanner ? (
				<div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/40 dark:bg-amber-950/20">
					<div className="mb-3 flex items-center gap-2">
						<Mic className="size-4 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden />
						<h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
							{t('pages.projectDetail.audioRecorder.recovery.title')}
						</h3>
					</div>

					<p className="mb-3 text-xs text-amber-800 dark:text-amber-300">
						{t('pages.projectDetail.audioRecorder.recovery.description')}
					</p>

					<ul className="space-y-2">
						{entries.map((entry) => (
							<li
								key={entry.id}
								className="flex min-w-0 items-center gap-2 rounded-md bg-white/60 px-3 py-2 text-xs dark:bg-black/20"
							>
								<div className="min-w-0 flex-1">
									<p className="break-words font-medium text-foreground">{entry.suggestedFilename}</p>
									<p className="mt-0.5 text-muted-foreground">
										{formatRecordingRelativeTime(entry.recordedAt)} &middot;{' '}
										{formatRecordingClock(entry.durationMs)} &middot;{' '}
										{formatRecordingByteSize(entry.blob.size)}
									</p>
								</div>

								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											type="button"
											variant="outline"
											size="icon"
											className="h-8 w-8 shrink-0 border-amber-200 bg-white/80 text-amber-900 hover:bg-white dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100 dark:hover:bg-amber-900/40"
											aria-label={t('pages.projectDetail.audioRecorder.recovery.actionsMenuAria')}
										>
											<MoreVertical className="size-4" aria-hidden />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end" className="min-w-44">
										<DropdownMenuItem className="cursor-pointer gap-2" onSelect={() => handleResume(entry)}>
											<Upload className="size-4" aria-hidden />
											{t('pages.projectDetail.audioRecorder.recovery.resume')}
										</DropdownMenuItem>
										<DropdownMenuItem className="cursor-pointer gap-2" onSelect={() => handleDownload(entry)}>
											<Download className="size-4" aria-hidden />
											{t('pages.projectDetail.audioRecorder.recovery.download')}
										</DropdownMenuItem>
										<DropdownMenuItem
											variant="destructive"
											className="cursor-pointer gap-2"
											disabled={isDiscarding}
											onSelect={() => setPendingDiscardId(entry.id)}
										>
											<Trash2 className="size-4" aria-hidden />
											{t('pages.projectDetail.audioRecorder.recovery.discard')}
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</li>
						))}
					</ul>
				</div>
			) : null}

			<AlertDialog
				open={pendingDiscardId !== null}
				onOpenChange={(open) => {
					if (!open && !isDiscarding) {
						setPendingDiscardId(null)
					}
				}}
			>
				<AlertDialogContent
					onEscapeKeyDown={(e) => {
						if (isDiscarding) e.preventDefault()
					}}
				>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('pages.projectDetail.audioRecorder.recovery.discardDialogTitle')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('pages.projectDetail.audioRecorder.recovery.discardConfirm')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDiscarding}>{t('common.cancel')}</AlertDialogCancel>
						<Button
							type="button"
							variant="destructive"
							disabled={isDiscarding}
							onClick={handleConfirmDiscard}
						>
							{t('pages.projectDetail.audioRecorder.recovery.discard')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
